import styled, { css } from 'styled-components';
import { BaSeTheme } from '../../theme';
import { ColorMapper } from '../../utils/color-transformation/color-mapper';
import { numberWithUnit } from '../../utils/string-utils';
import {
  ActionItemMoreInfoPosition,
  SingleItemStyledProps,
} from './action-item-props';

export interface ActionItemSingleItemProps {
  customColor: SingleItemStyledProps;
  indented?: boolean;
}

const handleNumberString = (val: string | number) => numberWithUnit(val);

export const ActionItemWrapper = styled.div<{ relative: boolean }>`
  ${(props) =>
    props.relative
      ? css`
          position: relative;
          display: inline-block;
        `
      : css`
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
        `}
`;

export const ActionItemContainer = styled.div<{
  minWidth: string | number;
  addStyles: string;
}>`
  ${(props) => css`
    min-width: ${handleNumberString(props.minWidth)};
  `}
  ${(props) => props.addStyles};
  position: absolute;
  background-color: ${BaSeTheme.colors.defaultColors.white};
  border: 1px solid ${ColorMapper.disabled.foreground};
  box-sizing: border-box;
  box-shadow: ${BaSeTheme.shadows.profundidade2};
  border-radius: 4px;
  transition: ${BaSeTheme.transitions.allPropertiesContinuousNormal};
  max-height: 0;
  overflow-y: hidden;
  visibility: hidden;
  z-index: ${BaSeTheme.stackOrder.belowOverlay};

  &.BaSe--action-item-open {
    max-height: 1000vh;
    visibility: visible;
  }
`;

export const ActionItemAccordionContent = styled.div`
  padding-left: 0;
`;

export const ActionItemListWrapper = styled.div`
  display: flex;
  flex-flow: column;
  padding: 8px 0;

  > .BaSe--accordion-content,
  .BaSe--accordion {
    overflow: hidden;
  }

  .BaSe--accordion-title-container {
    padding-right: 0.5em;
  }
`;

export const ActionItemTitleAccordionContainer = styled.div<ActionItemSingleItemProps>`
  cursor: pointer;
  padding: ${({ indented = false }) =>
    `8px 16px 8px ${indented ? '24px' : '16px'}`};
  width: 100%;
  text-align: left;

  display: flex;
  align-items: flex-start;
  border: none;
  outline: none;
  margin: 0;
  box-sizing: border-box;
  gap: 14px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-family: ${BaSeTheme.typography.fontFamily};
  font-size: ${BaSeTheme.typography.fontSizeBase};
  line-height: ${BaSeTheme.typography.lineHeightBase};

  img {
    width: 20px;
  }
`;

const actionItemSingleStyle = css<ActionItemSingleItemProps>`
  ${({ customColor, indented }) => css`
    font-family: ${BaSeTheme.typography.fontFamily};
    font-size: ${BaSeTheme.typography.fontSizeBase};
    line-height: ${BaSeTheme.typography.lineHeightBase};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    padding: 8px 16px 8px ${indented ? '24px' : '16px'};
    width: 100%;
    text-align: left;

    display: flex;
    align-items: flex-start;
    background-color: ${customColor?.backgroundColor};
    border: none;
    outline: none;
    color: inherit;
    text-decoration: none;
    cursor: ${customColor?.disabled ? 'not-allowed' : 'pointer'};
    margin: 0;
    box-sizing: border-box;
    gap: 14px;
    will-change: background-color;

    &,
    span {
      transition: ${BaSeTheme.transitions.allPropertiesContinuousFast};
    }

    img {
      width: 20px;
    }

    ${customColor?.hoverBackgroundColor &&
    css`
      &:hover,
      &:focus {
        background-color: ${customColor?.hoverBackgroundColor};
      }

      &:focus span {
        font-weight: bold;
      }
    `}
  `}
`;

export const ActionItemSingleItem = styled.button<ActionItemSingleItemProps>`
  ${actionItemSingleStyle};
`;

export const ActionItemSingleLink = styled.a<ActionItemSingleItemProps>`
  ${actionItemSingleStyle};
`;

export const ActionItemTextContent = styled.div`
  min-width: 0;
  display: flex;
  flex: 1;
  flex-flow: column;
  gap: 2px;
`;

export const ActionItemGroup = styled.div`
  display: flex;
  flex-flow: column;
`;

export const ActionItemGroupItems = styled.div``;

export const ActionItemGroupTitle = styled.div`
  padding: 0.5em 1em 0.25em;
  color: ${BaSeTheme.colors.institucionais.cinzaSebrae45};
  text-transform: uppercase;
`;

export const ActionItemGroupDivider = styled.div`
  margin: 0.25em 1em;
  border-top: 1px solid ${BaSeTheme.colors.institucionais.cinzaSebrae90};
`;

export const ActionItemHeaderInfo = styled.div<{
  position: ActionItemMoreInfoPosition;
}>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75em;
  padding: 0.75em;
  background-color: ${BaSeTheme.colors.institucionais.cinzaSebrae97};
  border-radius: ${({ position }) =>
    position === 'footer' ? '0 0 0.25em 0.25em' : '0.25em 0.25em 0 0'};
  margin: ${({ position }) =>
    position === 'footer' ? '0 0.5em 0.5em' : '0.5em 0.5em 0'};
`;

export const ActionItemHeaderInfoContent = styled.div`
  display: flex;
  flex: 1;
  flex-flow: column;
  gap: 2px;
`;

export const ButtonWrapperRelative = styled.div`
  position: relative;
`;
